/**
 * levels.js - what Bubble Pop asks a child to say, level by level.
 *
 * CONTENT, NOT MECHANICS. Nine levels, three each from Phases 1, 2 and 3.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS THE ENGINE'S AND WHAT IS THE GAME'S
 *
 * The engine owns everything that must mean the same thing in every game:
 * the phase, the level, its intent, the prompt STYLE, the recognition
 * strictness, the support ladder and its per-phase bounds, and which boxes the
 * level measures. None of that is repeated here, and none of it may be
 * overridden here. Read it with framework.getLevel / getPromptStyle /
 * getStrictness / getBoxesForLevel.
 *
 * This file owns what the engine cannot know: WHICH WORDS, in what
 * combinations, at what pace, with what on screen. That is the whole of a
 * game's content.
 *
 * The one field that looks like an override and is not: `nominalRung`. The
 * engine gives each phase a baseline and a floor/ceiling; a level within the
 * phase still has to sit somewhere between them, and the spec's own level
 * intents say where ("Partial word shown", "Image highlight only"). It is
 * asserted against the phase's bounds in validateContent().
 *
 * ---------------------------------------------------------------------------
 * WHY 3 LEVELS PER PHASE AND NOT ALL 17
 *
 * Section 8.7 lets a game cover a subset of PHASES. This game covers a subset
 * of LEVELS as well, which section 8.7 does not describe and the engine has no
 * field for - see docs/audit/GAME-01.md section 7.1. `COVERAGE` below is the
 * game's own declaration, and logic/placement.js is what keeps a child from
 * being placed on a level that does not exist here.
 *
 * ---------------------------------------------------------------------------
 * NO ROUND IS EVER THE SAME TWICE
 *
 * A level does not hold a fixed script of steps. It holds a POOL, and each
 * round draws from it (../../shared/rounds.js) avoiding what was just used. Two plays
 * of Phase 1 Level 1 are the same skill and different content. Section 8.5
 * requires this, and it is also the only way ten plays of a level - the window
 * the chart needs - stay tolerable to sit through.
 *
 * HOW WIDE A POOL HAS TO BE. The first playtest ran fifteen attempts at Phase 1
 * Level 1 and the child saw four words, because the pool held four. A box needs
 * ten first attempts to take a colour and levels are replayed until it does, so
 * a pool must comfortably outlast ten to fifteen draws: eight objects with a
 * four-round memory (../../shared/rounds.js MEMORY_ROUNDS) gives a cycle a child
 * cannot predict, where four gave one they could recite.
 */

/**
 * @typedef {object} Level
 * @property {string} id
 * @property {number} phase                framework phase (1-10)
 * @property {number} level                framework level within that phase
 * @property {string} scene                visual world; purely presentational
 * @property {string} note                 why this level is shaped this way
 * @property {string[]} pool               object ids the rounds draw from
 * @property {number} rounds               rounds in one pass of the level
 * @property {number} targetsPerRound      how many bubbles must be named
 * @property {number} bubblesOnScreen      targets plus distractors
 * @property {string} nominalRung          SUPPORT_LADDER id the trial opens at
 * @property {'none'|'first_letter'|'partial_word'} cueStyle
 *           how the partial_verbal rung is DRAWN. The rung is the engine's;
 *           its rendering is the game's.
 * @property {string[]} [carriers]         Phase 3: the carrier words offered
 * @property {object} pacing               the within-trial ladder walk, in ms
 *           from the end of the prompt: when the clue fires, when support
 *           escalates, and when the trial closes with no response.
 */

/** Default pacing. Per-level `pacing` overrides individual keys. */
export const DEFAULT_PACING = {
  hintAtMs: 4500,
  escalateAtMs: [8000, 12000],
  windowMs: 16000,
};

export const LEVELS = [
  // =========================================================================
  // PHASE 1 - Echoic Foundation. Pip says the word; the child says it back.
  // The ladder has exactly one rung here (the phase's bounds are 0..0), so
  // "escalation" is Pip modelling again, not a change of support.
  // =========================================================================
  {
    id: 'p1l1',
    phase: 1,
    level: 1,
    scene: 'meadow',
    note: 'One bubble, centre of the screen, nothing else moving. The spec’s '
        + '"1 object (center position)". Four very early nouns; the round '
        + 'picks one, so ten plays are not ten apples.',
    pool: ['apple', 'ball', 'dog', 'star', 'cup', 'egg', 'tree', 'pig'],
    rounds: 3,
    targetsPerRound: 1,
    bubblesOnScreen: 1,
    nominalRung: 'full_model',
    cueStyle: 'none',
    pacing: { hintAtMs: 5000, escalateAtMs: [7000], windowMs: 12000 },
  },
  {
    id: 'p1l2',
    phase: 1,
    level: 2,
    scene: 'seaside',
    note: 'One bubble, but it drifts and arrives anywhere on screen - the '
        + 'spec’s "1 object (different position)". The child has to find it '
        + 'before naming it, which is the only thing that changes from L1.',
    pool: ['car', 'sun', 'boat', 'hat', 'bear', 'key', 'box', 'cow'],
    rounds: 3,
    targetsPerRound: 1,
    bubblesOnScreen: 2,
    nominalRung: 'full_model',
    cueStyle: 'none',
    pacing: { hintAtMs: 5000, escalateAtMs: [7000], windowMs: 12000 },
  },
  {
    id: 'p1l3',
    phase: 1,
    level: 3,
    scene: 'night',
    note: 'Two bubbles, prompted one after the other - the spec’s "2 objects '
        + '(sequential prompts)". Both are on screen from the start, so the '
        + 'child must hold the second one while answering the first.',
    pool: ['moon', 'star', 'bird', 'fish', 'drum', 'cake', 'bus', 'cloud'],
    rounds: 2,
    targetsPerRound: 2,
    bubblesOnScreen: 3,
    nominalRung: 'full_model',
    cueStyle: 'none',
    pacing: { hintAtMs: 5000, escalateAtMs: [7500], windowMs: 13000 },
  },

  // =========================================================================
  // PHASE 2 - Single Words + Fading. Pip does NOT say the word first. The
  // trial opens with less support and walks down the ladder only if the child
  // needs it, which is the whole point: the rung that was live when they spoke
  // is what separates components A, B and C.
  //
  // The nominal rungs run 1, 1, 2 across the three levels - the spec's
  // "Partial word shown" / "First letter + image highlight" / "Image highlight
  // only". Levels 1 and 2 share a rung and differ in how much of the word the
  // cue shows, which is `cueStyle`.
  // =========================================================================
  {
    id: 'p2l1',
    phase: 2,
    level: 1,
    scene: 'meadow',
    note: 'The spec’s "Partial word shown (A___)". Same words the child met '
        + 'in Phase 1, so the only new demand is retrieving instead of '
        + 'echoing.',
    pool: ['apple', 'ball', 'dog', 'star', 'car', 'cup', 'egg', 'key'],
    rounds: 3,
    targetsPerRound: 1,
    bubblesOnScreen: 2,
    nominalRung: 'partial_verbal',
    cueStyle: 'partial_word',
    pacing: { hintAtMs: 4500, escalateAtMs: [9000], windowMs: 15000 },
  },
  {
    id: 'p2l2',
    phase: 2,
    level: 2,
    scene: 'garden',
    note: 'The spec’s "First letter + image highlight". Less of the word than '
        + 'L1 at the same rung; the bubble itself is brightened.',
    pool: ['cake', 'flower', 'frog', 'shoe', 'cloud', 'cat', 'pig', 'tree'],
    rounds: 3,
    targetsPerRound: 1,
    bubblesOnScreen: 3,
    nominalRung: 'partial_verbal',
    cueStyle: 'first_letter',
    pacing: { hintAtMs: 4500, escalateAtMs: [9000], windowMs: 15000 },
  },
  {
    id: 'p2l3',
    phase: 2,
    level: 3,
    scene: 'seaside',
    note: 'The spec’s "Image highlight only" - the trial opens at the visual '
        + 'rung with no letter at all. This is the first level where component '
        + 'C (produced independently) is reachable without the ladder having '
        + 'been walked, because the opening rung is not the full model.',
    pool: ['fish', 'boat', 'bird', 'train', 'bear', 'bus', 'box', 'moon'],
    rounds: 3,
    targetsPerRound: 1,
    bubblesOnScreen: 3,
    nominalRung: 'visual',
    cueStyle: 'none',
    pacing: { hintAtMs: 4500, escalateAtMs: [8500, 12500], windowMs: 17000 },
  },

  // =========================================================================
  // PHASE 3 - Two-Word Manding. The carrier decides what HAPPENS, which is
  // what makes the second word worth saying:
  //   want -> Pip catches it and passes it to the child
  //   put  -> it drops into the basket
  //   more -> another one of the same floats up
  // A game where every carrier does the same thing teaches the child that only
  // the noun matters. That is audit finding B43, and it is a content bug, not
  // a code one.
  // =========================================================================
  {
    id: 'p3l1',
    phase: 3,
    level: 1,
    scene: 'meadow',
    note: 'The spec’s "2 objects + want ___ frame". One carrier only, so the '
        + 'choice the child makes is which OBJECT, and the carrier is carried '
        + 'along with it.',
    pool: ['apple', 'ball', 'cake', 'bear', 'cup', 'egg', 'hat', 'cloud'],
    rounds: 3,
    targetsPerRound: 1,
    bubblesOnScreen: 2,
    carriers: ['want'],
    nominalRung: 'visual',
    cueStyle: 'none',
    pacing: { hintAtMs: 5000, escalateAtMs: [9000, 13000], windowMs: 17000 },
  },
  {
    id: 'p3l2',
    phase: 3,
    level: 2,
    scene: 'garden',
    note: 'The spec’s "2 objects + put ___ frame". The basket appears; "put" '
        + 'now does something visibly different from "want", which is the '
        + 'first evidence the child has that the carrier is doing work.',
    pool: ['flower', 'shoe', 'hat', 'drum', 'tree', 'key', 'box', 'pig'],
    rounds: 3,
    targetsPerRound: 1,
    bubblesOnScreen: 2,
    carriers: ['put'],
    nominalRung: 'visual',
    cueStyle: 'none',
    pacing: { hintAtMs: 5000, escalateAtMs: [9000, 13000], windowMs: 17000 },
  },
  {
    id: 'p3l3',
    phase: 3,
    level: 3,
    scene: 'night',
    note: 'The spec’s "3 objects, verb options shown". Both carriers are on '
        + 'offer and both are legal, so the child is choosing the verb as well '
        + 'as the noun. Either choice is correct - the level is not asking '
        + 'them to guess Pip’s mind, it is asking them to say what they want, '
        + 'which is what a mand is.',
    pool: ['star', 'moon', 'cloud', 'train', 'fish', 'cow', 'bus', 'flower'],
    rounds: 3,
    targetsPerRound: 1,
    bubblesOnScreen: 3,
    carriers: ['want', 'put'],
    nominalRung: 'visual',
    cueStyle: 'none',
    pacing: { hintAtMs: 5500, escalateAtMs: [9500, 13500], windowMs: 18000 },
  },
];

/**
 * The game's coverage declaration, in the shape the engine takes.
 *
 * Both halves are the engine's: `phasesAvailable` is section 8.7, and
 * `levelsAvailable` is the level dimension the engine gained after this game
 * showed that a level subset is the normal case rather than an exotic one
 * (GAME-01.md section 7.1). `placeInGame`, `getNextLevel` and
 * `getPreviousLevel` all honour it, so nothing downstream re-clamps.
 *
 * It must also match `games.engine_coverage` in the registry, which is what
 * the SERVER reads - coverage is a fact about the build, not something a
 * request may assert. A test asserts the two agree.
 */
export const COVERAGE = {
  phasesAvailable: [1, 2, 3],
  levelsAvailable: LEVELS.map((l) => ({ phase: l.phase, level: l.level })),
};

/** @param {number} phase @param {number} level @returns {Level|null} */
export function getLevelContent(phase, level) {
  return LEVELS.find((l) => l.phase === phase && l.level === level) || null;
}

/** Every scene id used, for preloading and for the test that asserts CSS exists. */
export const SCENES = [...new Set(LEVELS.map((l) => l.scene))];

export function pacingFor(level) {
  return { ...DEFAULT_PACING, ...(level.pacing || {}) };
}

export default { LEVELS, COVERAGE, DEFAULT_PACING, getLevelContent, pacingFor, SCENES };
